import React from 'react';
import PropTypes from 'prop-types';
import './Badge.css';

/**
 * Badge — a short status or category tag: "Levererad", "Vanlig patch",
 * "10% RABAT". Colour pairs are the DS ui tokens (text + background);
 * `neutral` is gray-075 / gray-9, `brand` gray-075 with the brand's alternative
 * primary — the promo chip over a photo («GRATIS», Crownstudent-MCP 69:19626).
 *
 * Sizes: 's' small-s padding at P.Small (a type chip — Figma OVER-LL 3047:44272), 'm' small-m padding (a status
 * pill), 'l' small-m padding at H6 (a promo tag). Shape: 'pill' rounds on the
 * brand's --radius-button (a full pill where buttons are round, a tag where they
 * are square), 'square' on --radius-inner-radius (an element inside a card).
 */
const Badge = ({ variant = 'neutral', size = 'm', shape = 'pill', icon, className = '', children, ...rest }) => (
  <span
    className={[
      'ds-badge',
      `ds-badge--${variant}`,
      `ds-badge--${size}`,
      `ds-badge--${shape}`,
      { s: 'typo-body-sm', l: 'typo-h6' }[size] || 'typo-label',
      className,
    ].filter(Boolean).join(' ')}
    {...rest}
  >
    {icon && <span className="ds-badge__icon" aria-hidden="true">{icon}</span>}
    <span className="ds-badge__text">{children}</span>
  </span>
);

Badge.propTypes = {
  /** Colour pair: neutral (gray), brand (gray-075 + the brand's alternative primary) or one of the DS ui tones */
  variant: PropTypes.oneOf(['neutral', 'brand', 'success', 'warning', 'error', 'info']),
  /** 's' chip · 'm' pill · 'l' tag at H6 */
  size: PropTypes.oneOf(['s', 'm', 'l']),
  /** 'pill' → --radius-button · 'square' → --radius-inner-radius (brand radii, never spacing) */
  shape: PropTypes.oneOf(['pill', 'square']),
  /** Leading icon (14–16px) */
  icon: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default Badge;
